import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
  Snackbar,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DeleteIcon from "@material-ui/icons/Delete";
import ImageIcon from "@material-ui/icons/Image";
import RefreshIcon from "@material-ui/icons/Refresh";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { useIntl } from "react-intl";
import { useBmapi } from "../utils/bmapi-context";
import { getErrorMessageString } from "../utils/errors";

export default function ImageManagerDialog({
  title = "Gestione immagini",
  folder = "notifications",
}) {
  const intl = useIntl();
  const { bmapi, notifyError } = useBmapi();

  const [open, setOpen] = useState(false);
  const [dirUrl, setDirUrl] = useState("");
  const [files, setFiles] = useState([]); // array di stringhe (nomi file)
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [selectedName, setSelectedName] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [copyToastOpen, setCopyToastOpen] = useState(false);

  const mountedRef = useRef(false);
  const triedRawSrcRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const ensureSlash = (u) => (u && u.endsWith("/") ? u : `${u || ""}/`);
  const buildEncodedUrl = (base, name) =>
    `${ensureSlash(base)}${encodeURIComponent(String(name))}`;
  const buildRawUrl = (base, name) => `${ensureSlash(base)}${String(name)}`;

  // normalizza files -> array di stringhe (nomi), dedup
  const normalizeNames = (arr) => {
    const names = (arr || []).map((it) =>
      typeof it === "string" ? it : it?.name || it?.filename || String(it ?? "")
    );
    return Array.from(new Set(names.filter(Boolean)));
  };

  const refresh = async () => {
    try {
      if (mountedRef.current) setLoading(true);
      const res = await (folder === "notifications"
        ? bmapi.listNotificationImages()
        : bmapi.listPostImages());

      const apiDir = res?.dir_url || ""; // <- usa sempre dir_url
      const apiFiles = normalizeNames(res?.files);

      if (!mountedRef.current) return;

      setDirUrl(apiDir);
      setFiles(apiFiles);

      // se c'è un selezionato, aggiorno la preview usando la nuova dir
      if (selectedName && apiDir) {
        triedRawSrcRef.current = false;
        setPreviewUrl(buildEncodedUrl(apiDir, selectedName));
      }
    } catch (e) {
      if (!mountedRef.current) return;
      setDirUrl("");
      setFiles([]);
      notifyError(getErrorMessageString(e, intl));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setSelectedName(null);
    setPreviewUrl(null);
    refresh();
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedName(null);
    setPreviewUrl(null);
  };

  const handleSelect = (name) => {
    if (!name) return;
    setSelectedName(name);
    triedRawSrcRef.current = false;
    // usa la dir corrente; se ancora vuota, non setto la preview
    if (dirUrl) {
      setPreviewUrl(buildEncodedUrl(dirUrl, name));
    } else {
      setPreviewUrl(null);
    }
  };

  const handleDelete = async (name) => {
    if (!name) return;
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Eliminare il file "${name}"?`)) return;

    try {
      if (mountedRef.current) setLoading(true);
      if (folder === "notifications") {
        await bmapi.deleteNotificationImages([name]);
      } else {
        await bmapi.deletePostImages([name]);
      }
      if (selectedName === name) {
        setSelectedName(null);
        setPreviewUrl(null);
      }
      await refresh();
    } catch (e) {
      notifyError(getErrorMessageString(e, intl));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const inputEl = e.currentTarget || e.target;
    const fl = inputEl?.files;
    if (!fl || fl.length === 0) return;

    try {
      if (mountedRef.current) setUploading(true);
      if (folder === "notifications") {
        await bmapi.uploadNotificationImages(fl);
      } else {
        await bmapi.uploadPostImages(fl);
      }
      await refresh();
    } catch (err) {
      notifyError(getErrorMessageString(err, intl));
    } finally {
      if (mountedRef.current) setUploading(false);
      if (inputEl && typeof inputEl.value !== "undefined") {
        try {
          inputEl.value = "";
        } catch (ex) {
          // alcuni browser impediscono l’assegnazione; ignora
          void ex;
        }
      }
    }
  };

  const handleCopyUrl = (name) => {
    if (!dirUrl || !name) return;
    const url = buildEncodedUrl(dirUrl, name);

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          if (mountedRef.current) setCopyToastOpen(true);
        })
        .catch(() => {
          window.prompt("Copia manualmente l'URL:", url);
          if (mountedRef.current) setCopyToastOpen(true);
        });
    } else {
      window.prompt("Copia manualmente l'URL:", url);
      if (mountedRef.current) setCopyToastOpen(true);
    }
  };

  const handleCopyToastClose = (event, reason) => {
    if (reason === "clickaway") return;
    setCopyToastOpen(false);
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<CloudUploadIcon />}
        onClick={handleOpen}
      >
        Carica immagini
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <ImageIcon style={{ marginRight: 8 }} />
            <Box component="span" fontWeight={600}>
              {title}
            </Box>
            <Box flexGrow={1} />
            <Tooltip title="Aggiorna">
              <IconButton onClick={refresh}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* ALERT MUI con URL directory */}
          <Box mb={2}>
            <Alert severity="info">
              <strong>Dir:</strong>{" "}
              {dirUrl ? (
                <a
                  href={dirUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ wordBreak: "break-all" }}
                >
                  {dirUrl}
                </a>
              ) : (
                <span style={{ opacity: 0.7 }}>non disponibile</span>
              )}
            </Alert>
          </Box>

          <Grid container spacing={2}>
            {/* Colonna sinistra: upload + lista */}
            <Grid item xs={12} md={5}>
              <Box mb={1} display="flex" alignItems="center" style={{ gap: 8 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  component="label"
                  disabled={uploading}
                >
                  {uploading ? "Carico..." : "Upload"}
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    multiple
                    onChange={handleUpload}
                  />
                </Button>
                {loading && <CircularProgress size={20} />}
              </Box>

              <Paper
                variant="outlined"
                style={{ maxHeight: 380, overflow: "auto" }}
              >
                {files.length === 0 && !loading ? (
                  <Box p={2}>
                    <Typography variant="body2">
                      Nessun file presente.
                    </Typography>
                  </Box>
                ) : (
                  <List dense>
                    {files.map((name) => (
                      <ListItem
                        key={name}
                        button
                        onClick={() => handleSelect(name)}
                        title="Clicca per anteprima"
                        selected={selectedName === name}
                      >
                        <ListItemText
                          primary={name}
                          secondary={
                            dirUrl ? buildEncodedUrl(dirUrl, name) : null
                          }
                        />
                        <ListItemSecondaryAction>
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-end"
                          >
                            <Tooltip title="Elimina">
                              <IconButton
                                edge="end"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  handleDelete(name);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>

                            {dirUrl && (
                              <Tooltip title="Copia URL">
                                <IconButton
                                  edge="end"
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    handleCopyUrl(name);
                                  }}
                                >
                                  <FileCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>

            {/* Colonna destra: anteprima */}
            <Grid item xs={12} md={7}>
              <Typography variant="subtitle2" gutterBottom>
                Anteprima
              </Typography>
              <Paper variant="outlined">
                <Box
                  height={360}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="#fafafa"
                >
                  {previewUrl ? (
                    <img
                      key={previewUrl}
                      src={previewUrl}
                      alt={`Anteprima ${selectedName || ""}`}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        display: "block",
                      }}
                      onError={(ev) => {
                        // fallback: prova URL non encodato una sola volta
                        if (!triedRawSrcRef.current && selectedName && dirUrl) {
                          triedRawSrcRef.current = true;
                          ev.currentTarget.src = buildRawUrl(
                            dirUrl,
                            selectedName
                          );
                        } else {
                          notifyError(
                            "Impossibile caricare l’immagine selezionata."
                          );
                        }
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Seleziona un file per vedere l’anteprima
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copyToastOpen}
        autoHideDuration={3000}
        onClose={handleCopyToastClose}
      >
        <Alert
          onClose={handleCopyToastClose}
          severity="success"
          elevation={6}
          variant="filled"
        >
          L&apos;URL dell&apos;immagine è stato copiato nella clipboard
        </Alert>
      </Snackbar>
    </>
  );
}
